import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import Swal from "sweetalert2";

export interface ResolvedApiError {
  title: string;
  message: string;
  /** Erros de validação campo a campo, quando o servidor os envia. */
  details: string[];
  /** Falhas transitórias (rede, timeout, 5xx) valem uma nova tentativa. */
  canRetry: boolean;
  status: number;
}

/**
 * Traduz uma falha HTTP em algo que o utilizador possa corrigir.
 *
 * O problema que resolve: o `error.interceptor` do projeto está comentado por
 * inteiro, por isso cada chamada tratava o erro à sua maneira — quase sempre
 * com uma mensagem fixa do género "Erro ao atualizar". O servidor manda o
 * motivo real (o NestJS devolve a chave i18n em `error.message`, e o
 * ValidationPipe devolve um array com o campo em falta), e essa informação
 * era deitada fora à porta.
 *
 * Regra: a mensagem do servidor manda sempre. Só quando não há nenhuma é que
 * se recorre ao estado HTTP, e cada estado diz o que fazer a seguir — não
 * apenas que correu mal.
 */
@Injectable({ providedIn: "root" })
export class ApiErrorService {
  constructor(private translate: TranslateService) {}

  /**
   * As mensagens do backend são chaves i18n (`t-...`). Traduz quando é chave e
   * conhecida; devolve o texto tal e qual quando já é uma frase.
   */
  private translateIfKey(value: string): string | null {
    const text = `${value ?? ""}`.trim();
    if (!text) {
      return null;
    }

    if (!/^t-[a-z0-9.\-]+$/i.test(text)) {
      return text;
    }

    const translated = this.translate.instant(text);
    // O ngx-translate devolve a própria chave quando não a conhece — nesse
    // caso mostrar "t-qualquer-coisa" ao utilizador seria pior que o genérico.
    return translated && translated !== text ? translated : null;
  }

  /**
   * Desembrulha o `message` até chegar a texto.
   *
   * O filtro de exceções do backend embrulha a resposta, por isso o corpo real
   * chega como `{ statusCode, message: { statusCode, message: "t-...", error } }`.
   * Ler `error.message` directamente devolvia um objeto e o ecrã mostrava
   * "[object Object]" — confirmado com um pedido real ao servidor.
   */
  private unwrapMessage(value: any, depth = 0): any {
    if (depth > 4 || value === null || value === undefined) {
      return value;
    }
    if (typeof value === "string" || Array.isArray(value)) {
      return value;
    }
    if (typeof value === "object") {
      return this.unwrapMessage(value.message ?? value.error, depth + 1);
    }
    return value;
  }

  private extractServerMessages(err: any): string[] {
    // O `jwt.interceptor` está registado e, para tudo o que não seja CMI,
    // substitui o HttpErrorResponse por `err.error.message || err.statusText`
    // — ou seja, por uma string solta. Sem este caso, a mensagem real do
    // servidor chegava aqui e era deitada fora, e o utilizador via
    // "Verifique a ligação" para um 422 de validação.
    // O corpo achatado pelo interceptor é `{ statusCode, message: "t-…",
    // error: "Unprocessable Entity" }`: a chave está em `message`, e `error`
    // é só o nome do estado HTTP. Olhar primeiro para `error` mostrava
    // "Unprocessable Entity" em vez da validação que falhou.
    const looksLikeKey = (value: any) =>
      typeof value === "string" && /^t-[a-z0-9.\-]+$/i.test(value.trim());
    const raw =
      typeof err === "string"
        ? err
        : looksLikeKey(err?.message) || Array.isArray(err?.message)
          ? err.message
          : this.unwrapMessage(
              err?.error?.message ?? err?.error ?? err?.message,
            );

    if (Array.isArray(raw)) {
      return raw
        .map((item: any) => this.translateIfKey(`${item}`))
        .filter((item): item is string => !!item);
    }

    if (typeof raw !== "string") {
      return [];
    }

    const single = this.translateIfKey(raw);
    return single ? [single] : [];
  }

  /**
   * Mensagem por estado HTTP quando o servidor não explica. Cada uma diz o que
   * o utilizador pode fazer — "sem permissão" e "verifique a ligação" levam a
   * acções diferentes, "algo correu mal" não leva a nenhuma.
   */
  private fallbackKeyForStatus(status: number): string {
    if (status === 0) return "t-error-network";
    if (status === 400 || status === 422) return "t-error-validation";
    if (status === 401) return "t-error-unauthorized";
    if (status === 403) return "t-error-forbidden";
    if (status === 404) return "t-error-not-found";
    if (status === 409) return "t-error-conflict";
    if (status === 413) return "t-error-too-large";
    if (status === 429) return "t-error-too-many";
    if (status >= 500) return "t-error-server";
    return "t-error-unknown";
  }

  resolve(err: any, fallbackMessage?: string): ResolvedApiError {
    const status = Number(
      err instanceof HttpErrorResponse ? err.status : (err?.status ?? 0),
    );

    const serverMessages = this.extractServerMessages(err);
    const [first, ...rest] = serverMessages;

    const message =
      first ??
      fallbackMessage ??
      this.translate.instant(this.fallbackKeyForStatus(status));

    // Quando o interceptor achata o erro numa string perde-se o estado HTTP.
    // Nesse caso o estado chega aqui como 0, que normalmente significa "sem
    // rede" — mas se o servidor explicou o motivo, houve resposta. Oferecer
    // "repetir" aí seria mandar o utilizador repetir um pedido que vai falhar
    // exactamente da mesma maneira.
    const explainedByServer = serverMessages.length > 0;
    const transient =
      status === 0 || status === 408 || status === 429 || status >= 500;

    return {
      title: this.translate.instant("t-something-wrong"),
      message,
      details: rest,
      // Falhas de rede e do servidor podem passar à segunda; um 403 ou um 422
      // não passam — repetir o mesmo pedido dá o mesmo erro.
      canRetry: transient && !(status === 0 && explainedByServer),
      status,
    };
  }

  /**
   * Apresenta o erro. Com `retry`, o diálogo ganha um botão de repetir — mas
   * só quando repetir tem hipótese de resultar.
   */
  show(err: any, options?: { fallback?: string; retry?: () => void }): void {
    const resolved = this.resolve(err, options?.fallback);
    const offerRetry = !!options?.retry && resolved.canRetry;

    const html = resolved.details.length
      ? `<p>${resolved.message}</p><ul style="text-align:left;margin:0;padding-left:1.1rem">${resolved.details
          .map((item) => `<li>${item}</li>`)
          .join("")}</ul>`
      : undefined;

    Swal.fire({
      title: resolved.title,
      text: html ? undefined : resolved.message,
      html,
      icon: resolved.status >= 500 || resolved.status === 0 ? "error" : "warning",
      confirmButtonColor: "rgb(54, 69, 116)",
      confirmButtonText: offerRetry
        ? this.translate.instant("t-retry")
        : this.translate.instant("t-close"),
      showCancelButton: offerRetry,
      cancelButtonText: this.translate.instant("t-close"),
      cancelButtonColor: "#74788d",
    }).then((result) => {
      if (offerRetry && result.isConfirmed) {
        options!.retry!();
      }
    });
  }
}
